import { useEffect, useState } from 'react';
import { LoadingState } from './LoadingState';
import { ErrorState } from './ErrorState';
import { formatViewCount } from '../utils/format';

type Props = { type: 'videos' | 'channels'; ids: [string, string] };
type VideoRecord = {
  video_id: string; title: string; channel_id: string; channel_title: string;
  thumbnail_url: string; duration_seconds: number; category_id: string | null;
  published_at: number | null; first_seen_at: number; last_seen_at: number; max_view_count: number;
};
type Trend = { delta_views: number; delta_per_hour: number; period_start: number; period_end: number; last_snapshot_at: number };
type ChannelTotals = { title: string; videoCount: number; totalViews: number; firstSeenAt: number };
type Item = { record?: VideoRecord; trend?: Trend | null; totals?: ChannelTotals; weeklyGrowth?: number; topVideos?: VideoRecord[]; channelId?: string };

const number = (value: unknown) => Number(value) || 0;
const date = (value: number | null | undefined) => value ? new Date(value * 1000).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : 'Unknown';
const duration = (seconds: number) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  return `${hours ? `${hours}:` : ''}${hours ? String(minutes).padStart(2, '0') : minutes}:${String(secs).padStart(2, '0')}`;
};
const ratio = (winner: number, other: number) => other > 0 ? `${(winner / other).toFixed(winner / other >= 10 ? 0 : 1)}×` : '';

export function ComparisonPage({ type, ids }: Props) {
  const [items, setItems] = useState<Item[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    fetch(`/api/compare?${new URLSearchParams({ type, a: ids[0], b: ids[1] })}`)
      .then(async (r) => { const d = await r.json() as { error?: string; items: Item[] }; if (!r.ok) throw new Error(d.error || 'Comparison unavailable'); return d; })
      .then((d) => setItems(d.items)).catch((e) => setError(e.message || 'Comparison unavailable')).finally(() => setLoading(false));
  }, [type, ids[0], ids[1]]);
  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} onRetry={() => location.reload()} />;
  const names = items.map((x) => type === 'videos' ? x.record!.title : x.totals!.title);
  const views = items.map((x) => type === 'videos' ? number(x.record!.max_view_count) : number(x.totals!.totalViews));
  const growth = items.map((x) => type === 'videos' ? number(x.trend?.delta_views) : number(x.weeklyGrowth));
  const winner = (values: number[]) => values[0] === values[1] ? -1 : values[0] > values[1] ? 0 : 1;
  const viewsWinner = winner(views);
  const growthWinner = winner(growth);
  const score = [0, 0];
  if (viewsWinner >= 0) score[viewsWinner]++;
  if (growthWinner >= 0) score[growthWinner]++;
  const overallWinner = winner(score);
  const metricRows = type === 'videos' ? [
    { label: 'Total tracked views', values: views, format: formatViewCount },
    { label: '7-day growth', values: growth, format: (v: number) => `+${formatViewCount(v)}` },
    { label: 'Growth per hour', values: items.map((x) => number(x.trend?.delta_per_hour)), format: (v: number) => `+${formatViewCount(v)}/hr` },
    { label: 'Video length', values: items.map((x) => number(x.record!.duration_seconds)), format: duration, higherWins: false },
  ] : [
    { label: 'Tracked views', values: views, format: formatViewCount },
    { label: '7-day growth', values: growth, format: (v: number) => `+${formatViewCount(v)}` },
    { label: 'Tracked videos', values: items.map((x) => number(x.totals!.videoCount)), format: (v: number) => v.toLocaleString() },
    { label: 'Views per tracked video', values: items.map((x) => number(x.totals!.totalViews) / Math.max(1, number(x.totals!.videoCount))), format: formatViewCount },
  ];
  return <>
    <div className="results-header"><h1 className="results-title">{type === 'videos' ? 'Video' : 'Channel'} comparison</h1></div>
    <p className="results-intro">A like-for-like comparison based on YouTubeMost snapshots. Growth covers the latest tracked 7-day window.</p>
    <section className="comparison-verdict">
      <span className="comparison-eyebrow">At a glance</span>
      <strong>{overallWinner < 0 ? 'Too close to call' : `${names[overallWinner]} leads`}</strong>
      <p>{overallWinner < 0 ? 'Each side wins one of the two headline metrics.' : `${names[overallWinner]} leads on both reach and recent momentum.`}</p>
    </section>
    <div className="comparison-grid">
      {items.map((x, i) => {
        const video = x.record;
        const youtubeUrl = type === 'videos' ? `https://www.youtube.com/watch?v=${ids[i]}` : `https://www.youtube.com/channel/${ids[i]}`;
        return <article className={`comparison-card ${overallWinner === i ? 'comparison-card-winner' : ''}`} key={ids[i]}>
          {overallWinner === i && <span className="comparison-winner-badge">Overall leader</span>}
          {type === 'videos' && video?.thumbnail_url && <img src={video.thumbnail_url} alt="" />}
          <h2>{names[i]}</h2>
          {type === 'videos' && <p className="comparison-subtitle">{video!.channel_title} · Published {date(video!.published_at)}</p>}
          {type === 'channels' && <p className="comparison-subtitle">Tracked since {date(x.totals!.firstSeenAt)}</p>}
          <a className="comparison-youtube-link" href={youtubeUrl} target="_blank" rel="noopener noreferrer">Open on YouTube ↗</a>
        </article>;
      })}
    </div>
    <section className="comparison-metrics" aria-label="Side-by-side metrics">
      <div className="comparison-metric-head"><span>Metric</span><strong>{names[0]}</strong><strong>{names[1]}</strong></div>
      {metricRows.map((row) => {
        const rowWinner = row.higherWins === false ? -1 : winner(row.values);
        return <div className="comparison-metric-row" key={row.label}>
          <span>{row.label}</span>
          {row.values.map((value, i) => <strong className={rowWinner === i ? 'metric-winner' : ''} key={i}>
            {row.format(value)} {rowWinner === i && <small>{ratio(value, row.values[1 - i])}</small>}
          </strong>)}
        </div>;
      })}
    </section>
    {type === 'channels' && <section className="comparison-top-videos">
      <h2>Top tracked videos</h2>
      <div className="comparison-grid">{items.map((x, i) => <div className="comparison-video-list" key={ids[i]}>
        <h3>{names[i]}</h3>
        <ol>{x.topVideos?.map((video) => <li key={video.video_id}>
          <a href={`https://www.youtube.com/watch?v=${video.video_id}`} target="_blank" rel="noopener noreferrer">{video.title}</a>
          <span>{formatViewCount(video.max_view_count)} views</span>
        </li>)}</ol>
      </div>)}</div>
    </section>}
  </>;
}
